import { useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { env } from "@/config/env";

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const IDLE_ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = ["mousemove", "keypress"];

export function useIdleLogout(
  timeoutMinutes = env.VITE_IDLE_TIMEOUT_MINUTES ?? DEFAULT_IDLE_TIMEOUT_MINUTES
) {
  const msal = useMsal();
  const accounts = Array.isArray(msal?.accounts) ? msal.accounts : [];
  const instance = msal?.instance;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (accounts.length === 0 || !instance) {
      if (accounts.length > 0 && !instance) {
        console.warn("Idle logout skipped: MSAL instance is unavailable.");
      }
      return;
    }

    const parsedTimeoutMinutes = Number(timeoutMinutes);
    const safeTimeoutMinutes = Number.isFinite(parsedTimeoutMinutes) && parsedTimeoutMinutes > 0
      ? parsedTimeoutMinutes
      : DEFAULT_IDLE_TIMEOUT_MINUTES;
    const timeoutMs = safeTimeoutMinutes * 60 * 1000;

    function clearTimer() {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    function resetTimer() {
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        const logoutRedirect = instance.logoutRedirect;
        if (typeof logoutRedirect === "function") {
          void logoutRedirect.call(instance);
          return;
        }

        console.warn("Idle logout skipped: MSAL instance.logoutRedirect is unavailable.");
      }, timeoutMs);
    }

    IDLE_ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, resetTimer);
    });

    resetTimer();

    return () => {
      clearTimer();
      IDLE_ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, resetTimer);
      });
    };
  }, [accounts.length, instance, timeoutMinutes]);
}